import { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { EditOutlined, DeleteOutlined } from "@mui/icons-material";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useUpdateTask, useDeleteTask } from "../hooks/useTasks";
import TaskDetailsModal from "./TaskDetailsModal";

const TaskCard = ({ task }) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [editedTask, setEditedTask] = useState(task);
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? "grabbing" : "grab",
  };

  const handleEdit = () => {
    setEditDialogOpen(true);
  };

  const handleDelete = () => {
    deleteTask.mutate(task.id);
  };

  const handleSaveEdit = () => {
    updateTask.mutate({
      id: task.id,
      title: editedTask.title,
      description: editedTask.description,
      column: task.column,
    });
    setEditDialogOpen(false);
  };

  const handleCancelEdit = () => {
    setEditedTask(task);
    setEditDialogOpen(false);
  };

  const handleCardClick = (event) => {
    // Don't open modal if clicking on menu button or during drag
    if (event.target.closest("button") || isDragging) return;
    setDetailsModalOpen(true);
  };

  const truncateText = (text, wordLimit = 8) => {
    if (!text) return "";
    const words = text.split(" ");
    if (words.length <= wordLimit) return text;
    return words.slice(0, wordLimit).join(" ") + "...";
  };

  return (
    <>
      <Card
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        onClick={handleCardClick}
        sx={{
          mb: 1.5,
          cursor: isDragging ? "grabbing" : "pointer",
          "&:hover": !isDragging
            ? {
                boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                transform: "translateY(-2px)",
              }
            : {},
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          backgroundColor: isDragging ? "#f8f9fa" : "white",
          border: isDragging ? "2px solid #2196f3" : "1px solid #e0e0e0",
          boxShadow: isDragging
            ? "0 8px 32px rgba(33, 150, 243, 0.3)"
            : "0 1px 3px rgba(0,0,0,0.1)",
          transform: isDragging ? "rotate(2deg) scale(1.05)" : "none",
          zIndex: isDragging ? 1000 : 1,
        }}
      >
        <CardContent sx={{ position: "relative", pb: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Box sx={{ flex: 1, pr: 1 }}>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                {task.title}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {truncateText(task.description)}
              </Typography>
            </Box>
          </Box>
        </CardContent>

        <Box sx={{ display: "flex", justifyContent: "flex-end", pr: 1, pb: 1 }}>
          <IconButton
            size="small"
            onClick={handleEdit}
            sx={{
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.08)",
              },
              width: 32,
              height: 32,
            }}
          >
            <EditOutlined fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={handleDelete}
            sx={{
              "&:hover": {
                backgroundColor: "rgba(244, 67, 54, 0.1)",
                color: "error.main",
              },
              width: 32,
              height: 32,
            }}
          >
            <DeleteOutlined fontSize="small" />
          </IconButton>
        </Box>
      </Card>

      <Dialog
        open={editDialogOpen}
        onClose={handleCancelEdit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Task</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            fullWidth
            variant="outlined"
            value={editedTask.title}
            onChange={(e) =>
              setEditedTask({ ...editedTask, title: e.target.value })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={editedTask.description}
            onChange={(e) =>
              setEditedTask({ ...editedTask, description: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <TaskDetailsModal
        task={task}
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        onEdit={() => setEditDialogOpen(true)}
      />
    </>
  );
};

export default TaskCard;
